import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Typography,
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  getDashboardCampaigns         as getCampaigns,
  getDashboardCureAgingSummary  as getCureSummary,
  getDashboardCureAgingByDpd    as getCureByDpd,
  getDashboardCureAgingByDay    as getCureByDay,
  getDashboardCureAgingTiming   as getCureTiming,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const RED    = "#A32D2D";
const GREY   = "#888780";

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last",
];

// cure_type taxonomy from the proc.
const CURE_TYPES  = ["Confirmed", "DPD-Signal", "Re-age", "NewDelinq"];
const TYPE_COLOR  = { "Confirmed": GREEN, "DPD-Signal": BLUE, "Re-age": AMBER, "NewDelinq": RED };
const TYPE_LABEL  = { "Confirmed": "Confirmed", "DPD-Signal": "DPD signal", "Re-age": "Re-age", "NewDelinq": "New delinq" };

const DPD_BANDS  = ["1-9","10-19","20-29","30-39","40-49","50-59","60-89","90+"];
const DPD_COLORS = {
  "1-9": "#3DBE92", "10-19": "#1D9E75", "20-29": "#378ADD", "30-39": "#185FA5",
  "40-49": "#BA7517", "50-59": "#D4870F", "60-89": "#D4537E", "90+": "#A01F50",
};

// days-to-cure buckets — count key + dollar key + display label + color.
// Order matters: the cure curve accumulates left-to-right.
const DTC_BUCKETS = [
  { key: "dtc_0_3",    amtKey: "dtc_0_3_amt",    label: "0-3",   color: "#3DBE92" },
  { key: "dtc_4_7",    amtKey: "dtc_4_7_amt",    label: "4-7",   color: "#1D9E75" },
  { key: "dtc_8_14",   amtKey: "dtc_8_14_amt",   label: "8-14",  color: "#378ADD" },
  { key: "dtc_15_30",  amtKey: "dtc_15_30_amt",  label: "15-30", color: "#BA7517" },
  { key: "dtc_31plus", amtKey: "dtc_31plus_amt", label: "31+",   color: "#A32D2D" },
];

// ── Date helpers (shared shape with the other dashboards) ──────────
function toLocal(d) {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${dy}`;
}

function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":       start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":       start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":       start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":       start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "C Wk":     start = thisMonday; end = now; break;
    case "CW+Last":  start = lastMonday; end = now; break;
    case "This Mo":  start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last": start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo": start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:         start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const mm = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return mm ? new Date(+mm[1], +mm[2] - 1, +mm[3]) : new Date(s);
  };
  const start = parse(startDate), end = parse(endDate);
  const durMs = end.getTime() - start.getTime(), dayMs = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Format helpers ─────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)      return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtInt(n) { return safeNum(n).toLocaleString(); }
function fmtPct(v, dec = 1) { const n = Number(v); return isNaN(n) ? "—" : `${n.toFixed(dec)}%`; }

function fmtDay(s) {
  const d = new Date(String(s).replace(/-/g, "/"));
  if (isNaN(d)) return String(s);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
}

function buildDelta(cur, prev, fmtVal) {
  const c = Number(cur), p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct = ((c - p) / p) * 100, up = pct > 0;
  return { up, good: up, text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${fmtVal ? fmtVal(p) : p.toLocaleString()}` };
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>{children}</Typography>
  );
}

function KpiTile({ label, value, sub, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 12px", minWidth: 0,
      display: "flex", flexDirection: "column", gap: "4px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {sub != null && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.2 }}>{sub}</Typography>
      )}
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.25 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>{title}</Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  const boundaries = new Set();
  if (groups) { let idx = 0; groups.forEach((sz, gi) => { if (gi < groups.length - 1) { idx += sz; boundaries.add(idx); } }); }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {boundaries.has(i) && <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px", cursor: "pointer", userSelect: "none",
                border: "0.5px solid", borderColor: active ? BLUE : "divider",
                background: active ? BLUE : "transparent", color: active ? "#fff" : "text.secondary",
                fontWeight: active ? 500 : 400, transition: "all .15s", "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── In-segment % label — only drawn when the segment is wide enough ──
// recharts passes geometry (x/y/width/height) plus the LabelList's resolved
// `value`. We skip empty strings and thin slices so labels never overlap.
function SegmentPct(props) {
  const { x, y, width, height, value } = props;
  const text = value == null ? "" : String(value);
  if (!text || width < 26) return null;   // skip thin slices to avoid overlap
  return (
    <text x={x + width / 2} y={y + height / 2} fill="#fff"
      fontSize={11} fontWeight={600} textAnchor="middle" dominantBaseline="central"
      pointerEvents="none">{text}</text>
  );
}

// ── Cure-quality funnel (horizontal bars, narrowing) ───────────────
function CureFunnel({ summary, isDollars }) {
  const v = (cnt, amt) => (isDollars ? safeNum(amt) : safeNum(cnt));

  const all       = v(summary.total_count,      summary.total_delinquency_amount);
  const resolved  = v(
    safeNum(summary.confirmed_count) + safeNum(summary.dpd_signal_count) + safeNum(summary.reage_count),
    safeNum(summary.confirmed_amount) + safeNum(summary.dpd_signal_amount) + safeNum(summary.reage_amount),
  );
  const cures     = v(summary.cure_count, summary.cure_amount);
  const confirmed = v(summary.confirmed_count, summary.confirmed_amount);

  const steps = [
    { label: "All events",      val: all,       color: GREY  },
    { label: "Resolved",        val: resolved,  color: AMBER },
    { label: "True cures",      val: cures,     color: BLUE  },
    { label: "Payment-confirmed", val: confirmed, color: GREEN },
  ];
  const denom = all || 1;
  const fmt = isDollars ? fmtCurrency : fmtInt;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {steps.map(s => {
        const pct = (s.val / denom) * 100;
        const onBar = pct >= 45;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, background: "#f0f0f0", borderRadius: "4px", height: 28, position: "relative" }}>
              <Box sx={{ height: "100%", borderRadius: "4px", width: `${pct}%`, background: s.color, transition: "width .4s ease" }} />
              {s.val > 0 && (
                <Box sx={{
                  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 500, color: onBar ? "#fff" : s.color, pointerEvents: "none",
                }}>{fmt(s.val)}</Box>
              )}
            </Box>
            <Box sx={{ width: 130, textAlign: "right", fontSize: 12, color: "text.secondary" }}>
              {s.label}<br />
              <span style={{ color: s.color, fontWeight: 500 }}>{pct.toFixed(1)}%</span>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Custom legend (small squares, matches the app) ─────────────────
function Legendish({ items }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1, fontSize: 12, color: "text.secondary" }}>
      {items.map(it => (
        <Box key={it.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: it.color }} />
          {it.label}
        </Box>
      ))}
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function CureAgingDashboard() {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [metric,      setMetric]      = useState("Count");   // Count | Dollars
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [summary,     setSummary]     = useState({});
  const [prevSummary, setPrevSummary] = useState({});
  const [byDpd,       setByDpd]       = useState([]);
  const [byDay,       setByDay]       = useState([]);
  const [timing,      setTiming]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");

  const isDollars = metric === "Dollars";

  useEffect(() => {
    getCampaigns()
      .then(d => setCampaigns(["All", ...(d || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign: campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
  }), [rangePreset, campaign, gran]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign: campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
  }), [params.startDate, params.endDate, campaign, gran]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, ps, d, day, tm] = await Promise.all([
        getCureSummary(params),
        getCureSummary(prevParams).catch(() => ({})),
        getCureByDpd(params),
        getCureByDay(params),
        getCureTiming(params).catch(() => []),
      ]);
      setSummary(s    || {});
      setPrevSummary(ps || {});
      setByDpd(d      || []);
      setByDay(day    || []);
      setTiming(tm    || []);
    } catch (e) {
      console.error("Cure-aging load error:", e?.response?.status, e?.message);
      setError(`Failed to load cure-aging data: ${e?.response?.data?.error || e?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── KPI values ──
  const k = useMemo(() => {
    const s = summary, p = prevSummary;
    const cureCount = safeNum(s.cure_count);
    const cureAmt   = safeNum(s.cure_amount);
    const confRate  = cureCount > 0 ? (safeNum(s.confirmed_count) / cureCount) * 100 : 0;
    const pConfRate = safeNum(p.cure_count) > 0 ? (safeNum(p.confirmed_count) / safeNum(p.cure_count)) * 100 : 0;
    return {
      cureCount, cureAmt, confRate,
      cureCountDelta: buildDelta(cureCount, p.cure_count, fmtInt),
      cureAmtDelta:   buildDelta(cureAmt,   p.cure_amount, fmtCurrency),
      confPayDelta:   buildDelta(s.total_confirmed_payment, p.total_confirmed_payment, fmtCurrency),
      confRateDelta:  buildDelta(confRate,  pConfRate, v => `${v.toFixed(1)}%`),
    };
  }, [summary, prevSummary]);

  // ── Trend (stacked by cure_type over time) ──
  const trendData = useMemo(() => {
    const field = isDollars ? "total_delinquency_amount" : "total_count";
    const byKey = new Map();
    for (const r of byDay) {
      const key = String(r.summary_date).slice(0, 10);
      if (!byKey.has(key)) {
        const seed = { _key: key, label: fmtDay(key) };
        CURE_TYPES.forEach(t => { seed[t] = 0; });
        byKey.set(key, seed);
      }
      const row = byKey.get(key);
      if (row[r.cure_type] !== undefined) row[r.cure_type] += safeNum(r[field]);
    }
    return [...byKey.values()].sort((a, b) => a._key.localeCompare(b._key));
  }, [byDay, isDollars]);

  // ── Aging by DPD (horizontal, stacked Confirmed + DPD-Signal + Re-age) ──
  // Each row carries `_total` (the stacked sum) and `_pctLabel` (that band's
  // share of all charted cures), rendered at the end of the bar. Each row
  // also carries per-segment shares (`_pct_Confirmed` etc.) — each cure
  // type's % of its own band's total — rendered inside the colored segment.
  const agingData = useMemo(() => {
    const cSuffix = isDollars ? "_amount" : "_count";
    const byBand = Object.fromEntries(byDpd.map(r => [r.dpd_range, r]));
    const rows = DPD_BANDS.map(b => {
      const r = byBand[b] || {};
      const conf  = safeNum(r[`confirmed${cSuffix}`]);
      const sig   = safeNum(r[`dpd_signal${cSuffix}`]);
      const reage = safeNum(r[`reage${cSuffix}`]);
      return { dpd: b, Confirmed: conf, "DPD-Signal": sig, "Re-age": reage, _total: conf + sig + reage };
    });
    const grand = rows.reduce((a, r) => a + r._total, 0) || 1;
    return rows.map(r => {
      const denom = r._total || 1;
      const seg = (v) => (r._total > 0 && v > 0 ? `${((v / denom) * 100).toFixed(0)}%` : "");
      return {
        ...r,
        _cap: 0,   // transparent stack cap — anchors the label at the bar's end
        _pctLabel: r._total > 0 ? `${((r._total / grand) * 100).toFixed(1)}%` : "",
        "_pct_Confirmed":  seg(r.Confirmed),
        "_pct_DPD-Signal": seg(r["DPD-Signal"]),
        "_pct_Re-age":     seg(r["Re-age"]),
      };
    });
  }, [byDpd, isDollars]);

  // ── Time to cure: lookup, avg-days, bucket distribution, cure curve ──
  const timingByBand = useMemo(() => Object.fromEntries((timing || []).map(r => [r.dpd_range, r])), [timing]);

  // Avg-days horizontal bar (one row per band; days_sum / cure_count).
  const avgDaysData = useMemo(() => {
    return DPD_BANDS.map(b => {
      const r = timingByBand[b] || {};
      const cnt = safeNum(r.cure_count);
      return { dpd: b, avgDays: cnt > 0 ? safeNum(r.days_sum) / cnt : 0, cures: cnt };
    });
  }, [timingByBand]);

  // Per-band distribution across day buckets — count OR dollars per the toggle.
  // Each row also carries `_pct_<bucket>` = that bucket's % of the band's total
  // cures, rendered inside the segment (width-gated so it stays legible).
  const bucketDistData = useMemo(() => {
    return DPD_BANDS.map(band => {
      const r = timingByBand[band] || {};
      const row = { dpd: band };
      let total = 0;
      for (const bk of DTC_BUCKETS) {
        const v = safeNum(isDollars ? r[bk.amtKey] : r[bk.key]);
        row[bk.label] = v;
        total += v;
      }
      const denom = total || 1;
      for (const bk of DTC_BUCKETS) {
        row[`_pct_${bk.label}`] = total > 0 && row[bk.label] > 0
          ? `${((row[bk.label] / denom) * 100).toFixed(0)}%`
          : "";
      }
      return row;
    });
  }, [timingByBand, isDollars]);

  // Cumulative cure curve: % cured by elapsed-day bucket, one line per band.
  // Always count-based — % of that band's cures resolved by the bucket's max day.
  const cureCurveData = useMemo(() => {
    return DTC_BUCKETS.map((bk, i) => {
      const point = { label: bk.label };
      for (const band of DPD_BANDS) {
        const r = timingByBand[band] || {};
        const total = safeNum(r.cure_count);
        if (total <= 0) { point[band] = null; continue; }
        let cum = 0;
        for (let j = 0; j <= i; j++) cum += safeNum(r[DTC_BUCKETS[j].key]);
        point[band] = (cum / total) * 100;
      }
      return point;
    });
  }, [timingByBand]);

  // Bands that actually have cures — only plot/legend those so it isn't noisy.
  const activeBands = useMemo(
    () => DPD_BANDS.filter(b => safeNum(timingByBand[b]?.cure_count) > 0),
    [timingByBand]
  );

  const truncatedTotal = useMemo(
    () => (timing || []).reduce((a, r) => a + safeNum(r.dtc_truncated), 0),
    [timing]
  );

  const fmtAxis = isDollars ? (v => fmtCurrency(v)) : (v => v);
  const tipFmt  = (v, n) => [isDollars ? fmtCurrency(v) : fmtInt(v), TYPE_LABEL[n] || n];
  const axisStyle = { fontSize: 10, fill: "#888" };

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup label="Date range" options={RANGE_OPTIONS} value={rangePreset} onChange={setRangePreset} groups={[3, 2, 2, 2]} />
        <PillGroup label="Measure" options={["Count", "Dollars"]} value={metric} onChange={setMetric} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)} size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading cure-aging data…
        </Box>
      )}

      {!loading && !error && (
        <>
          {/* ── Cure KPIs ── */}
          <SectionTitle>Cures (confirmed + DPD signal)</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiTile label="Cured customers" value={fmtInt(k.cureCount)}
              sub={fmtCurrency(k.cureAmt) + " resolved"} color={BLUE} delta={k.cureCountDelta} />
            <KpiTile label="Confirmed payments" value={fmtCurrency(summary.total_confirmed_payment)}
              sub={fmtInt(summary.confirmed_count) + " confirmed"} color={GREEN} delta={k.confPayDelta} />
            <KpiTile label="DPD-signal cures" value={fmtInt(summary.dpd_signal_count)}
              sub={fmtCurrency(summary.dpd_signal_amount)} color={BLUE} />
            <KpiTile label="Confirmed rate" value={fmtPct(k.confRate)}
              sub="of cures payment-backed" color={GREEN} delta={k.confRateDelta} />
          </Box>

          {/* ── Other signals ── */}
          <SectionTitle>Other signals (not counted as cures)</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiTile label="Re-age (DPD recalc)" value={fmtInt(summary.reage_count)}
              sub={fmtCurrency(summary.reage_amount) + " — artifact, review"} color={AMBER} />
            <KpiTile label="New delinquencies" value={fmtInt(summary.newdelinq_count)}
              sub={fmtCurrency(summary.newdelinq_amount) + " — DPD leapt up"} color={RED} />
          </Box>

          {/* ── Trend + funnel ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={`Cures over time — ${isDollars ? "dollars" : "count"} by type`} height={280}>
              <Legendish items={CURE_TYPES.map(t => ({ label: TYPE_LABEL[t], color: TYPE_COLOR[t] }))} />
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
                  <YAxis tick={axisStyle} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={tipFmt} />
                  {CURE_TYPES.map(t => (
                    <Bar key={t} dataKey={t} name={t} stackId="ct" fill={TYPE_COLOR[t]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Cure quality funnel" height={280}>
              <CureFunnel summary={summary} isDollars={isDollars} />
            </ChartCard>
          </Box>

          {/* ── Time to cure — first-seen to cure ── */}
          <SectionTitle>Time to cure — days from first seen</SectionTitle>

          {/* Avg days (headline) + bucket distribution (count/$ via the toggle) */}
          <Box sx={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Avg days to cure by DPD band" height={340}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={avgDaysData} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                  <XAxis type="number" tick={axisStyle} />
                  <YAxis type="category" dataKey="dpd" tick={axisStyle} width={48} />
                  <Tooltip contentStyle={{ fontSize: 12 }}
                    formatter={(v, n, p) => [`${v.toFixed(1)} days`, `Avg (${fmtInt(p?.payload?.cures)} cures)`]} />
                  <Bar dataKey="avgDays" fill={BLUE} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={`Cures by time bucket — ${isDollars ? "dollars" : "count"}`} height={340}>
              <Legendish items={DTC_BUCKETS.map(bk => ({ label: bk.label + "d", color: bk.color }))} />
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={bucketDistData} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                  <XAxis type="number" tick={axisStyle} tickFormatter={fmtAxis} />
                  <YAxis type="category" dataKey="dpd" tick={axisStyle} width={48} />
                  <Tooltip contentStyle={{ fontSize: 12 }}
                    formatter={(v, n) => [isDollars ? fmtCurrency(v) : fmtInt(v), n + "d"]} />
                  {DTC_BUCKETS.map(bk => (
                    <Bar key={bk.label} dataKey={bk.label} name={bk.label} stackId="t" fill={bk.color}>
                      <LabelList dataKey={`_pct_${bk.label}`} content={SegmentPct} />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* Cure curve — % cured by elapsed days (always percentages) */}
          <ChartCard title="Cure curve — % cured by elapsed days" height={320}>
            <Legendish items={activeBands.map(b => ({ label: b, color: DPD_COLORS[b] }))} />
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={cureCurveData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="label" tick={axisStyle} />
                <YAxis tick={axisStyle} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip contentStyle={{ fontSize: 12 }}
                  formatter={(v, n) => [v == null ? "—" : `${v.toFixed(1)}%`, n]} />
                {activeBands.map(b => (
                  <Line key={b} type="monotone" dataKey={b} name={b}
                    stroke={DPD_COLORS[b]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          {truncatedTotal > 0 && (
            <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5, mt: 0.75 }}>
              Note: {fmtInt(truncatedTotal)} cures were already mid-delinquency when they first
              appeared in the window, so their days-to-cure is a floor (true age predates the data).
            </Typography>
          )}

          {/* ── Aging by DPD — horizontal ── */}
          <SectionTitle>Aging — cures by DPD band</SectionTitle>
          <ChartCard title={`${isDollars ? "Dollars" : "Count"} cured by days-past-due`} height={360}>
            <Legendish items={[
              { label: "Confirmed", color: GREEN },
              { label: "DPD signal", color: BLUE },
              { label: "Re-age", color: AMBER },
            ]} />
            <ResponsiveContainer width="100%" height="88%">
              <BarChart data={agingData} layout="vertical" margin={{ top: 4, right: 52, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} tickFormatter={fmtAxis} />
                <YAxis type="category" dataKey="dpd" tick={axisStyle} width={48} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tipFmt} />
                <Bar dataKey="Confirmed"  stackId="a" fill={GREEN}>
                  <LabelList dataKey="_pct_Confirmed" content={SegmentPct} />
                </Bar>
                <Bar dataKey="DPD-Signal" stackId="a" fill={BLUE}>
                  <LabelList dataKey="_pct_DPD-Signal" content={SegmentPct} />
                </Bar>
                <Bar dataKey="Re-age"     stackId="a" fill={AMBER}>
                  <LabelList dataKey="_pct_Re-age" content={SegmentPct} />
                </Bar>
                <Bar dataKey="_cap" stackId="a" fill="transparent" isAnimationActive={false} legendType="none" tooltipType="none">
                  <LabelList dataKey="_pctLabel" position="right"
                    style={{ fontSize: 11, fill: "#666", fontWeight: 500 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </Box>
  );
}